import { WSText } from "@wingspanhq/fe-component-library";
import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import { useHistory } from "react-router-dom";
import {
  getPayerRequirementDetailUrl,
  PAYERS_REQUIREMENTS_PATH
} from "../../../../Payers/paths";
import { getRequirementDefinitionName } from "../../../../RequirementDefinitions/utils";
import { NotificationWithGroup } from "../../../selectors/selectorAlerts";
import { NotificationHandleType } from "../../Notifications";

export const GROUP_THRESHOLD = 2;

export const getRequirementNameOrReadableType = (
  notification: NotificationWithGroup
): string => {
  const requirementType = notification.context?.requirement?.requirementType;
  return (
    notification.context?.requirement?.name ||
    getRequirementDefinitionName({
      type: requirementType,
      // TODO(ivanh): add context and policy type here
      ...(notification.context.requirement as any).dataSourceMetadata
    })
  );
};

export const getGroupedRequirementNames = (
  notifications: NotificationWithGroup
): string => {
  const completeGeneralRequirements: RequirementType[] = [];

  return notifications.group
    .filter(notificaiton => {
      switch (notificaiton.context?.requirement?.requirementType) {
        case RequirementType.Registration:
        case RequirementType.TaxVerification:
        case RequirementType.PayoutMethod:
          if (
            !completeGeneralRequirements.includes(
              notificaiton.context.requirement.requirementType
            )
          ) {
            completeGeneralRequirements.push(
              notificaiton.context.requirement.requirementType
            );

            return true;
          }

          return false;

        default:
          return true;
      }
    })
    .map(g => g.context?.requirement?.name)
    .join(", ");
};

export const createRequirementAction = (
  notification: NotificationWithGroup,
  history: ReturnType<typeof useHistory>
) => {
  const isGrouped = notification.group.length >= GROUP_THRESHOLD;

  return {
    text: isGrouped ? "View requirements" : "View requirement",
    onClick: () => {
      if (isGrouped) {
        history.push(PAYERS_REQUIREMENTS_PATH);
      } else {
        history.push(
          getPayerRequirementDetailUrl(
            notification.context?.payer.payerId,
            notification.context?.requirement.requirementId
          )
        );
      }
    }
  };
};

export const createRequirementTitle = (
  notification: NotificationWithGroup,
  status: string
): string => {
  const requirementType = notification.context?.requirement?.requirementType;
  const isGrouped = notification.group.length >= GROUP_THRESHOLD;
  const readableType = getRequirementDefinitionName({
    type: requirementType,
    // TODO(ivanh): add context and policy type here
    ...(notification.context.requirement as any).dataSourceMetadata
  });

  switch (status) {
    case NotificationHandleType.RequirementsApprovedPayee:
      return isGrouped
        ? "Multiple requirements approved by client"
        : `${readableType} is now complete`;
    case NotificationHandleType.RequirementsRejectedPayee:
      return isGrouped ? "Multiple requirements rejected" : readableType;
    case NotificationHandleType.RequirementsAwaitingReviewPayee:
      return isGrouped
        ? "Multiple requirements awaiting clients"
        : readableType;
    case NotificationHandleType.RequirementsReRequestedPayee:
      return isGrouped ? "Multiple requirements re-requested" : readableType;
    case NotificationHandleType.RequirementsCompletePayee:
      return isGrouped
        ? "Multiple requirements complete"
        : `${readableType} is now complete`;
    default:
      return readableType;
  }
};

export const createRequirementChildren = (
  notification: NotificationWithGroup,
  status: string
): React.ReactNode => {
  const isGrouped = notification.group.length >= GROUP_THRESHOLD;
  const requirementNameOrReadableType =
    getRequirementNameOrReadableType(notification);
  const groupedNames = getGroupedRequirementNames(notification);
  const payerName = notification.context?.payer?.payerName;

  switch (status) {
    case NotificationHandleType.RequirementsApprovedPayee:
      return isGrouped ? (
        <WSText.ParagraphSm color="gray600">
          {groupedNames} requirement was approved by client, and is now
          complete.
        </WSText.ParagraphSm>
      ) : (
        <WSText.ParagraphSm color="gray600">
          {requirementNameOrReadableType} requirement was approved by client,{" "}
          <b>{payerName}</b>, and is now complete.
        </WSText.ParagraphSm>
      );
    case NotificationHandleType.RequirementsRejectedPayee:
      return isGrouped ? (
        <WSText.ParagraphSm color="gray600">
          {groupedNames} requirement was rejected by clients.
        </WSText.ParagraphSm>
      ) : (
        <WSText.ParagraphSm color="gray600">
          {requirementNameOrReadableType} requirement was rejected by client,{" "}
          <b>{payerName}</b>.
        </WSText.ParagraphSm>
      );
    case NotificationHandleType.RequirementsAwaitingReviewPayee:
      return isGrouped ? (
        <WSText.ParagraphSm color="gray600">
          {groupedNames} are now awaiting clients. There is no additional action
          required at the moment. You will be notified of the outcome and any
          next steps.
        </WSText.ParagraphSm>
      ) : (
        <WSText.ParagraphSm color="gray600">
          {requirementNameOrReadableType} is now awaiting client,{" "}
          <b>{payerName}</b>. There is no additional action required at the
          moment. You will be notified of the outcome and any next steps.
        </WSText.ParagraphSm>
      );
    case NotificationHandleType.RequirementsReRequestedPayee:
      return isGrouped ? (
        <WSText.ParagraphSm color="gray600">
          {groupedNames} requirement was re-requested by clients.
        </WSText.ParagraphSm>
      ) : (
        <WSText.ParagraphSm color="gray600">
          {requirementNameOrReadableType} requirement was re-requested by
          client, <b>{payerName}</b>.
        </WSText.ParagraphSm>
      );
    case NotificationHandleType.RequirementsCompletePayee:
      return isGrouped ? (
        <WSText.ParagraphSm color="gray600">
          {groupedNames} requirement was completed by you.
        </WSText.ParagraphSm>
      ) : (
        <WSText.ParagraphSm color="gray600">
          {requirementNameOrReadableType} requirement was completed by you.
        </WSText.ParagraphSm>
      );
    default:
      return "";
  }
};
